/**
 * Re-key a sidebar project snapshot onto ONE runtime environment.
 *
 * Extracted from `buildRuntimeProjectGroups.ts`, which grew past its own cap:
 * this is a self-contained transform (project + environmentId in, a re-keyed
 * project or null out) whose only caller is `addRemoteProjectGroup` /
 * `partitionRuntimeProjects`, which stayed behind.
 */
import type { EnvironmentId } from "@cmmd-forge/contracts";
import type { SidebarProjectSnapshot } from "./SidebarProjectItem";

function toRuntimeProjectKey(environmentId: EnvironmentId, projectKey: string): string {
  return `runtime:${environmentId}:${projectKey}`;
}

function runtimeProjectMemberRefs(
  project: SidebarProjectSnapshot,
  environmentId: EnvironmentId,
  members: SidebarProjectSnapshot["worktreeMembers"],
) {
  return members.length > 0
    ? members.map((member) => member.projectRef)
    : project.memberProjectRefs.filter((ref) => ref.environmentId === environmentId);
}

function hasRuntimeProject(
  project: SidebarProjectSnapshot,
  environmentId: EnvironmentId,
  representative: SidebarProjectSnapshot["worktreeMembers"][number] | null,
  memberRefs: SidebarProjectSnapshot["memberProjectRefs"],
): boolean {
  return (
    representative !== null ||
    project.environmentId === environmentId ||
    memberRefs.some((ref) => ref.environmentId === environmentId)
  );
}

function runtimeActiveWorktreeKey(
  project: SidebarProjectSnapshot,
  members: SidebarProjectSnapshot["worktreeMembers"],
): string | null {
  if (project.activeWorktreeKey === null) return null;
  return members.some((member) => member.worktreeKey === project.activeWorktreeKey)
    ? project.activeWorktreeKey
    : null;
}

export function cloneProjectForRuntimeEnvironment(
  project: SidebarProjectSnapshot,
  environmentId: EnvironmentId,
  mode: "environment-only" | "local-with-cloud" = "environment-only",
): SidebarProjectSnapshot | null {
  const members = project.worktreeMembers.filter(
    (member) =>
      member.environmentId === environmentId ||
      (mode === "local-with-cloud" && member.environmentRole === "cloud"),
  );
  const memberRefs = runtimeProjectMemberRefs(project, environmentId, members);
  const representative = members.find((member) => member.environmentId === environmentId) ?? null;
  if (!hasRuntimeProject(project, environmentId, representative, memberRefs)) return null;

  const projectRef = memberRefs.find((ref) => ref.environmentId === environmentId) ?? {
    environmentId,
    projectId: project.id,
  };
  const runtimeProjectKey = toRuntimeProjectKey(environmentId, project.projectKey);
  const activeWorktreeKey = runtimeActiveWorktreeKey(project, members);

  return {
    ...project,
    environmentId,
    id: projectRef.projectId,
    cwd: representative?.cwd ?? project.cwd,
    projectKey: mode === "local-with-cloud" ? project.projectKey : runtimeProjectKey,
    memberProjectRefs: memberRefs,
    worktreeMembers: members,
    activeWorktreeKey,
  };
}
